import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import { useTabInventoryState } from 'app/components/pages/Extranet/ExtranetInventory/Modern/hooks';
import { computePacks } from 'app/components/pages/Extranet/ExtranetInventory/Modern/utils/packs';
import { hotelPath } from 'app/hooks/data/hotels/useHotel';
import type { InventoryResponse } from 'app/typings/core/extranet/inventory/inventory';
import { formatDateWithoutHour } from 'app/utils/dates';
import { apiMutation } from 'app/utils/request/api';

import { CORE_QUERY_KEYS as HOTEL_QUERY_KEYS } from '../../hotel/index';

import { CORE_QUERY_KEYS, ENDPOINTS } from '..';

export function useDisable(
  hotelId: number,
  options: { startDate: string; endDate: string }
) {
  const queryClient = useQueryClient();
  const { setInventory, dependencies } = useTabInventoryState();

  return useMutation({
    mutationFn: async () =>
      apiMutation('DELETE', {
        path: ENDPOINTS.disableAutoInventory,
        params: { hotelId: hotelId.toString() },
        queryParams: options,
      }) as Promise<InventoryResponse>,
    onSuccess: (response) => {
      queryClient.setQueryData(
        [CORE_QUERY_KEYS.inventory, { hotelId, ...options }],
        (oldData: InventoryResponse | undefined) => {
          if (!oldData) {
            return response;
          }

          const newData = Object.entries(oldData).reduce(
            (acc, [date, dateData]) => {
              acc[date] = {
                ...dateData,
                rooms: response[date]?.rooms ?? dateData.rooms,
              };

              return acc;
            },
            {} as InventoryResponse
          );

          if (dependencies) {
            const newInventory = computePacks(
              formatDateWithoutHour(dependencies.saleEndDate),
              options.startDate,
              options.endDate,
              dependencies.packData,
              dependencies.filteredExperiences,
              [],
              newData,
              dependencies.hasChannelManager,
              // Auto inventory has just been disabled.
              false
            );

            setInventory(newInventory);
          }

          return newData;
        }
      );

      queryClient.invalidateQueries({ queryKey: [hotelPath, { hotelId }] });
      queryClient.invalidateQueries({
        queryKey: [HOTEL_QUERY_KEYS.hotelRooms, { id: hotelId }],
      });
    },
    onError: () => {
      message.error('Failed to disable auto inventory');
    },
  });
}
